import {
  Component,
  Inject,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  IonContent,
  Platform,
  PopoverController,
  ModalController,
} from '@ionic/angular';
import { ApiService } from 'src/app/services/api.service';
import { SelectListComponent } from 'src/app/components/select-list/select-list.component';

import { SeoService } from 'src/app/services/seo.service';
import { PostItem, PostListingData } from 'src/app/types/post';
import { environment } from 'src/environments/environment';
import { CategoryItem } from 'src/app/types/category';
import { SEOSchema, SEOStoreCategory } from 'src/app/types/seo';
import { JsonLdService } from 'src/app/services/json-ld.service';
import { isPlatformBrowser } from '@angular/common';
import { GlobalDataService } from 'src/app/services/global-data.service';
import {
  ButtonIcon,
  IconPosition,
} from 'src/app/components/button/button.component';
import { Icon } from 'src/app/components/icons/svg-icon.component';

interface StoreSection {
  alphabet: string;
  record: PostItem[];
}

@Component({
  selector: 'app-stores-listing',
  templateUrl: './stores-listing.page.html',
  styleUrls: ['./stores-listing.page.scss'],
})
export class StoresListingPage implements OnInit {
  @ViewChild(IonContent) content: IonContent;

  cordova: boolean = false;
  desktop: boolean = false;
  isBrowser: boolean = false;
  filteredBy: string;
  selectedCategory: CategoryItem;
  stores: PostItem[];
  currentDayName: string;
  metaKeywords: string;
  environmentName: string = environment.environment_title;
  isExpanded: boolean = false;
  isModalOpen = false;

  data: PostListingData = {
    title: 'Shop Listing',
    excerpt:
      environment.environment_name === 'capegate'
        ? 'Capegate Shopping Centre has a wide selection of unique stores. Come visit us today - there is something for the whole family.'
        : environment.environment_name === 'glen'
        ? 'The Glen Shopping Centre offers you multiple options when it comes to buying the newest and trendiest footwear out there. View our catalogue of shoe stores!'
        : environment.environment_name === 'woodlands'
        ? 'Ready to go shopping? With over 400 shops at Woodland Shopping Centre selling Fashion, footwear, home décor, jewellery, movies, health & beauty, and much more.'
        : environment.environment_name === 'somerset'
        ? 'Ready to go shopping? With over 400 shops at Somerset Mall Shopping Centre selling Fashion, footwear, home décor, jewellery, movies, health & beauty, and more.'
        : environment.environment_name === 'hydepark'
        ? 'Ready to go shopping? With over 400 shops at Hyde Park Corner selling Fashion, footwear, home décor, jewellery, movies, health & beauty, and much, much more.'
        : environment.environment_name === 'clearwater'
        ? 'Clearwater Mall has a variety of stores for you to shop until you drop! come to our shopping mall in Roodepoort for the latest trends to meet your fashion needs'
        : `Search more than 400 shops at ${environment.environment_title} Shopping Centre.  Fashion, footwear, home d&eacute;cor, jewellery, movies, health &amp; beauty and much, much more.`,
  };

  schema: SEOSchema = {
    '@context': 'http://schema.org',
    '@type': 'ItemList',
    name: environment.environment_title,
    url: environment.environment_url,
  };

  filters: string[] = [
    '@',
    'A',
    'B',
    'C',
    'D',
    'E',
    'F',
    'G',
    'H',
    'I',
    'J',
    'K',
    'L',
    'M',
    'N',
    'O',
    'P',
    'Q',
    'R',
    'S',
    'T',
    'U',
    'V',
    'W',
    'X',
    'Y',
    'Z',
  ];

  menuIcon: ButtonIcon = {
    name: Icon.List,
    position: IconPosition.LEFT,
  };

  modalCloseIcon: ButtonIcon = {
    name: Icon.Close,
    position: IconPosition.RIGHT,
  };

  constructor(
    private platform: Platform,
    private globalData: GlobalDataService,
    private API: ApiService,
    private seo: SeoService,
    private route: ActivatedRoute,
    private router: Router,
    public modalController: ModalController,
    public popoverController: PopoverController,
    private jsonLdService: JsonLdService,
    @Inject(PLATFORM_ID) platformId: string
  ) {
    this.isBrowser = isPlatformBrowser(platformId);

    // Get current day name for relevant opening times
    this.currentDayName = new Date()
      .toLocaleDateString('en-US', {
        weekday: 'long',
      })
      .toLowerCase();

    this.platform.ready().then(() => {
      this.cordova = this.platform.is('cordova');
      this.desktop =
        this.platform.is('desktop') ||
        (typeof window !== 'undefined' && window.innerWidth > 1200);
    });

    this.platform.resize.subscribe(async () => {
      this.desktop =
        this.platform.is('desktop') ||
        (typeof window !== 'undefined' && window.innerWidth > 1200);
    });
  }

  /**
   * TrackBy for parent items
   */
  trackByParent(index: number, group: any): number {
    return group.alphabet;
  }

  /**
   * TrackBy for child items
   */
  trackByChild(index: number, item: any): number {
    return item.slug;
  }

  calculateMallSEOTitle() {
    let seoTitle =
      'All Shops | Store Directory | ' +
      environment.environment_title +
      ' Shopping Centre';

    if (SEOStoreCategory[environment.environment_name]?.default_title) {
      seoTitle = SEOStoreCategory[environment.environment_name].default_title;
    }

    return seoTitle;
  }

  calculateMallSEODescription(description: string) {
    let seoDescription = description;

    if (SEOStoreCategory[environment.environment_name]?.default_description) {
      seoDescription =
        SEOStoreCategory[environment.environment_name].default_description;
    }

    return seoDescription;
  }

  calculateCategorySEOTitle() {
    let seoTitle =
      (this.selectedCategory.seoTitle &&
      this.selectedCategory.seoTitle.length > 0
        ? this.selectedCategory.seoTitle
        : this.selectedCategory.title) +
      ' | ' +
      environment.environment_title +
      ' Shopping Centre';

    if (
      this.selectedCategory?.slug &&
      SEOStoreCategory[environment.environment_name]
    ) {
      seoTitle =
        this.selectedCategory.title +
        SEOStoreCategory[environment.environment_name].title_extention;
    }

    return seoTitle;
  }

  calculateCategorySEODescription() {
    let seoDescription = this.selectedCategory.seoDescription;

    if (
      this.selectedCategory?.slug &&
      SEOStoreCategory[environment.environment_name] &&
      SEOStoreCategory[environment.environment_name][this.selectedCategory.slug]
    ) {
      seoDescription =
        SEOStoreCategory[environment.environment_name][
          this.selectedCategory.slug
        ];
    }

    return seoDescription;
  }

  /**
   * Set Category SEO
   */
  setCategorySEO(allCategories = false) {
    let schemaData = { ...this.schema };

    if (allCategories) {
      // Title tag
      const seoTitle = this.calculateMallSEOTitle();
      this.seo.setTitle(seoTitle);
      schemaData = {
        ...schemaData,
        name: seoTitle,
      };

      // Description tag
      const seoData = {
        title: seoTitle,
        description: this.calculateMallSEODescription(this.data.excerpt),
        keywords: this.metaKeywords + ' stores',
      };
      this.seo.setMeta(seoData);
      this.seo.setSEOMeta(seoData);
    } else if (this.selectedCategory) {
      // Title tag
      const seoTitle = this.calculateCategorySEOTitle();
      this.seo.setTitle(seoTitle);
      schemaData = {
        ...schemaData,
        name: seoTitle,
      };

      // Description tag
      const seoData = {
        title: seoTitle,
        description: this.calculateCategorySEODescription(),
        keywords: this.metaKeywords + ' stores',
      };
      this.seo.setMeta(seoData);
      this.seo.setSEOMeta(seoData);
    }

    this.schema = { ...this.schema, ...schemaData };

    this.jsonLdService.insertSchema(this.schema);
  }

  /**
   * Category filter popover
   */
  async presentPopover(ev: any) {
    const popover = await this.popoverController.create({
      component: SelectListComponent,
      componentProps: {
        data: this.data.categories,
      },
      event: ev,
      translucent: true,
    });

    popover.onWillDismiss().then((popoverData) => {
      if (popoverData.data === 'all') {
        this.resetAllFilters();
        this.setCategorySEO(true);
      } else {
        const filterCategory = {
          title: popoverData.data?.title,
          slug: popoverData.data?.slug,
          seoTitle: popoverData.data?.seoTitle,
          seoDescription: popoverData.data?.seoDescription,
        };
        if (popoverData.data && this.selectedCategory != filterCategory) {
          this.selectedCategory = filterCategory;

          if (this.selectedCategory?.slug) {
            this.resetLetterFilter();
            this.filterStores();

            if (this.filteredBy) {
              window?.history.pushState(
                {},
                '',
                `/shops/${this.selectedCategory?.slug}/${this.filteredBy}`
              );
            } else {
              window?.history.pushState(
                {},
                '',
                `/shops/${this.selectedCategory?.slug}`
              );
            }
          }

          this.setCategorySEO();
        }
      }
    });

    return await popover.present();
  }

  /**
   * Link to store detail
   */
  linkToDetail(item) {
    this.router.navigateByUrl(`/shop/${item.slug}`);
  }

  /**
   * Filter stores according to starting letter & selected category
   */
  filterStores() {
    let allStores: PostItem[] = this.data?.items || [];

    // Apply filteredBy filter (e.g., if it's set to a letter)
    if (this.filteredBy) {
      if (this.filteredBy === '@') {
        allStores = allStores.filter(
          (e) =>
            e.title[0].toLowerCase() === this.filteredBy ||
            /\d/.test(e.title[0])
        );
      } else {
        allStores = allStores.filter(
          (e) => e.title[0].toLowerCase() === this.filteredBy
        );
      }
    }

    // Apply category filter if a selected category exists
    if (this.selectedCategory?.slug) {
      allStores = allStores.filter((item) => {
        return (
          item.category?.slug === this.selectedCategory.slug ||
          item.category?.child?.slug === this.selectedCategory.slug ||
          (Array.isArray(item.subCategories) &&
            item.subCategories.some(
              (cat) =>
                cat.slug === this.selectedCategory.slug ||
                cat.child?.slug === this.selectedCategory.slug
            ))
        );
      });
    }

    // Update filtered stores
    this.stores = allStores;
  }

  /**
   * Sort items by selected starting letter
   */
  sortByLetter() {
    return this.data?.items
      ? this.data.items.sort((a, b) => a.title.localeCompare(b.title))
      : [];
  }

  /**
   * Set filter letter & update URL
   */
  filterByLetter(letter, updateURL = false) {
    this.resetCategoryFilter();
    this.filteredBy = letter.toLowerCase();

    if (updateURL) {
      window?.history.pushState({}, '', `/shops/${this.filteredBy}`);

      if (this.selectedCategory?.slug) {
        window?.history.pushState(
          {},
          '',
          `/shops/${this.selectedCategory?.slug}/${this.filteredBy}`
        );
      } else {
        window?.history.pushState({}, '', `/shops/${this.filteredBy}`);
      }
    }

    this.filterStores();
  }

  /**
   * Reset all filters
   */
  resetAllFilters() {
    this.filteredBy = '';
    this.selectedCategory = null;
    this.stores = this.sortByLetter();
    window?.history.pushState({}, '', `/shops`);
  }

  /**
   * Reset letter filter only
   */
  resetLetterFilter() {
    this.filteredBy = '';
    this.filterStores();
    if (this.selectedCategory?.slug) {
      window?.history.pushState(
        {},
        '',
        `/shops/${this.selectedCategory?.slug}`
      );
    } else {
      window?.history.pushState({}, '', `/shops`);
    }
  }

  /**
   * Reset category filter only
   */
  resetCategoryFilter() {
    this.selectedCategory = null;
    this.filterStores();
    this.setCategorySEO(true);

    if (this.filteredBy) {
      window?.history.pushState({}, '', `/shops/${this.filteredBy}`);
    } else {
      window?.history.pushState({}, '', `/shops`);
    }
  }

  /**
   * Filter stores according to selected filter & category
   */
  defaultFilter(updateURL = false) {
    // Filter by first letter of title if startsWith is set
    let startsWith = this.route.snapshot.paramMap.get('startsWith');
    let categorySlug = this.route.snapshot.paramMap.get('categorySlug');

    // CategorySlug and StartsWith filters are on the same level
    // If CategorySlug is a single character it is intended to be StartsWith
    if (!startsWith && categorySlug && categorySlug.length === 1)
      startsWith = categorySlug;

    if (categorySlug && categorySlug.length > 1) {
      this.selectedCategory = {
        slug: categorySlug,
      };
    } else {
      this.selectedCategory = null;
    }

    if (this.selectedCategory?.slug && !this.selectedCategory?.title) {
      for (let i = 0; i < this.data.categories.length; i++) {
        if (this.data.categories[i].slug === this.selectedCategory.slug) {
          this.selectedCategory = this.data.categories[i];
          break;
        } else if (this.data.categories[i].children) {
          for (let k = 0; k < this.data.categories[i].children.length; k++) {
            if (
              this.data.categories[i].children[k].slug ===
              this.selectedCategory.slug
            ) {
              this.selectedCategory = this.data.categories[i].children[k];
              break;
            }
          }
        }
      }
    }

    this.setCategorySEO();

    if (startsWith) {
      this.filterByLetter(startsWith, updateURL);
    } else {
      this.stores = this.sortByLetter();

      setTimeout(() => {
        this.filterStores();
      }, 0);
    }
  }

  /**
   * Get store current status
   */
  async getStatuses() {
    this.API.GET(`public/stores/statuses/${environment.mall_id}`).subscribe({
      next: (response) => {
        this.data.items = this.data.items.map((item) => {
          let relevantItem = response.filter(
            (match) => match.slug === item.slug
          )[0].tradingHours;

          if (relevantItem) {
            relevantItem = relevantItem.split(' - ');
            item.typeTrading[this.currentDayName].open_text = relevantItem[0];
            item.typeTrading[this.currentDayName].close_text = relevantItem[1];
          }

          return {
            ...item,
            currentStatus: response.filter(
              (match) => match.slug === item.slug
            )[0].currentStatus,
          };
        });

        this.defaultFilter();
      },
      error: () => {},
    });
  }

  /**
   * Return store opening to closing times
   */
  storeOpenClose(item) {
    const element = item.typeTrading[this.currentDayName];
    if (element) {
      return element.open_text + ' - ' + element.close_text;
    }
  }

  ngOnInit() {
    this.initializeData();
    this.getCategories();
    this.metaKeywords = this.globalData.getGlobalData().general.metaKeywords;

    this.route.data.subscribe((response: any) => {
      if (response?.data) {
        this.data.items = response.data.results;
        this.data.categories = this.reorderCategories(this.data.categories);
        this.defaultFilter();
      }

      if (!this.cordova) {
        this.setSchemaData();
        this.setSEOMetadata();
      }
    });
  }

  /**
   * Initialize component data
   */
  private initializeData() {
    this.data = { categories: [], items: [], title: '', excerpt: '' };
  }

  /**
   * Fetch categories from the API and update component data
   */
  async getCategories() {
    this.API.GET(
      `public/categories/mall/${environment.mall_id}/store-categories`
    ).subscribe({
      next: (response) => {
        if (response) {
          this.data.categories = response
            .sort((a, b) => a.title.localeCompare(b.title))
            .map((cat) => {
              cat.children?.sort((c, d) => c.title.localeCompare(d.title));
              return cat;
            });

          this.defaultFilter();
        }
      },
      error: () => {
        // Handle error
      },
    });
  }

  /**
   * Set schema data for SEO
   */
  private setSchemaData() {
    if (!this.data?.items?.length) return;

    this.schema = {
      ...this.schema,
      numberOfItems: this.data.items.length,
      itemListElement: this.data.items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Store',
          name: item.title,
          image: item.logoImage,
          telephone: item.typeGeneral.telephone_number || undefined,
          email: item.typeGeneral.email_address || undefined,
          brand: { logo: item.logoImage, name: item.title },
        },
      })),
    };

    this.jsonLdService.insertSchema(this.schema);
  }

  /**
   * Set SEO metadata
   */
  private setSEOMetadata() {
    const seoTitle = this.calculateMallSEOTitle();
    const seoDescription = this.calculateMallSEODescription(this.data.excerpt);
    const initialMallData = this.globalData.getGlobalData();

    const seoData = {
      title: seoTitle,
      description: seoDescription,
      keywords: `${this.metaKeywords} stores`,
      socialImage: initialMallData?.social?.openGraphImage,
    };

    this.seo.setTitle(seoTitle);
    this.seo.setMeta(seoData);
    this.seo.setSEOMeta(seoData);

    this.schema = {
      ...this.schema,
      name: seoTitle,
      description: seoDescription,
    };
  }

  // ngOnInit() {
  //   this.data = {
  //     categories: [],
  //     items: [],
  //     title: '',
  //     excerpt: '',
  //   };
  //   // Fetch categories from the API

  //   this.getCategories();

  //   const initialMallData = this.globalData.getGlobalData();

  //   this.metaKeywords = initialMallData.general.metaKeywords;
  //   this.route.data.subscribe((response: any) => {
  //     if (response?.data) {
  //       this.data = {
  //         ...this.data,
  //         // categories: response.data.categories
  //         //   .sort((a, b) => (a.title > b.title ? 1 : -1))
  //         //   .map((cat) => {
  //         //     cat.children.sort((c, d) => (c.title > d.title ? 1 : -1));
  //         //     return cat;
  //         //   }),

  //         items: response.data.results,
  //       };

  //       // Move "New Stores" to the first position
  //       this.data.categories = this.reorderCategories(this.data.categories);

  //       this.defaultFilter();
  //     }

  //     // Set Meta & Social tags
  //     if (!this.cordova) {
  //       let schemaData = { ...this.schema };

  //       if (this.data?.items?.length > 0) {
  //         let itemListElement = [];

  //         for (let index = 0; index < this.data.items.length; index++) {
  //           itemListElement.push({
  //             '@type': 'ListItem',
  //             position: index + 1,
  //             item: {
  //               '@type': 'Store',
  //               name: this.data.items[index].title,
  //               image: this.data.items[index].logoImage,
  //               telephone:
  //                 this.data.items[index].typeGeneral.telephone_number ||
  //                 undefined,
  //               email:
  //                 this.data.items[index].typeGeneral.email_address || undefined,
  //               brand: {
  //                 logo: this.data.items[index].logoImage,
  //                 name: this.data.items[index].title,
  //               },
  //             },
  //           });
  //         }

  //         schemaData = {
  //           ...schemaData,
  //           numberOfItems: this.data.items.length,
  //           itemListElement: itemListElement,
  //         };
  //       }

  //       // Title tag
  //       const seoTitle = this.calculateMallSEOTitle();
  //       this.seo.setTitle(seoTitle);
  //       schemaData = {
  //         ...schemaData,
  //         name: seoTitle,
  //       };

  //       // Description tag
  //       const seoDescription = this.calculateMallSEODescription(
  //         this.data.excerpt
  //       );
  //       const seoData = {
  //         title: seoTitle,
  //         description: seoDescription,
  //         keywords: this.metaKeywords + ' stores',
  //         socialImage: initialMallData?.social?.openGraphImage,
  //       };
  //       this.seo.setMeta(seoData);
  //       this.seo.setSEOMeta(seoData);
  //       schemaData = {
  //         ...schemaData,
  //         description: seoDescription,
  //       };

  //       this.schema = { ...this.schema, ...schemaData };
  //       this.jsonLdService.insertSchema(this.schema);
  //     }
  //   });
  // }

  /**
   * Fetch categories from the API and update the component data
   */
  // async getCategories() {
  //   this.API.GET(
  //     `public/categories/mall/${environment.mall_id}/store-categories`
  //   ).subscribe({
  //     next: (response) => {
  //       if (response) {
  //         this.data = {
  //           ...this.data,
  //           categories: response
  //             .sort((a, b) => (a.title > b.title ? 1 : -1))
  //             .map((cat) => {
  //               cat.children?.sort((c, d) => (c.title > d.title ? 1 : -1));
  //               return cat;
  //             }),
  //         };

  //         this.defaultFilter();
  //       }
  //     },
  //     error: () => {
  //       // Handle error
  //     },
  //   });
  // }

  /**
   * Reorder categories to place "New Stores" first
   */
  reorderCategories(categories: any[]): any[] {
    const newStoresIndex = categories.findIndex(
      (category) => category.slug === 'new-stores'
    );

    if (newStoresIndex > -1) {
      const [newStores] = categories.splice(newStoresIndex, 1);
      categories.unshift(newStores);
    }

    return categories;
  }

  ionViewWillEnter() {
    if (this.isBrowser) this.getStatuses();

    this.content.scrollToTop();
  }

  ionViewWillLeave() {
    this.filteredBy = '';
    this.selectedCategory = null;
    this.stores = this.sortByLetter();
  }

  searchAgain() {
    this.resetCategoryFilter();

    this.content.scrollToTop();
  }

  filterByCategory(category: any) {
    if (category.slug === 'all') {
      this.resetAllFilters();
    } else {
      const selected = this.data.categories.find(
        (cat) => cat.slug === category.slug
      );
      if (selected) {
        this.selectedCategory = selected;
        this.filterStores();
      }
    }

    // Update URL and SEO
    const url = this.selectedCategory?.slug
      ? `/shops/${this.selectedCategory.slug}`
      : `/shops`;
    window.history.pushState({}, '', url);

    this.setCategorySEO();
  }

  toggleCategoryView() {
    this.isExpanded = !this.isExpanded;
  }

  openCategoryFilterModal() {
    this.isModalOpen = true;
  }

  closeCategoryFilterModal() {
    this.isModalOpen = false;
  }

  applyFilter() {
    if (this.selectedCategory.slug) {
      this.filterByCategory(this.selectedCategory);
    } else {
      this.resetAllFilters();
    }
    this.closeCategoryFilterModal();
  }

  getFilteredRecords(): number {
    return this.stores ? this.stores.length : 0;
  }
}
